import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import EquityCurveInline from '../../components/EquityCurveInline';
import PageLayout from '../../components/PageLayout';
import { getBacktestRun, getBacktestRunChartData, getBacktestTrace, getBacktestExplain, addBacktestIndicators, filterBacktestTrades, listFilterPresets, createFilterPreset } from '../../api/botsApi';
import { fetchAvailableIndicators } from '../../api/api';
import TradingChart from '../../components/TradingChart';
import CustomSelect from '../../components/ui/CustomSelect';
import IndicatorControls from '../../components/IndicatorControls';
import IndicatorSelector from '../../components/bots/sectioned-builder/IndicatorSelector';
import { Button } from '../../components/ui/button';
import { useMemo } from 'react';
import TraceFilters from '../../components/bots/TraceFilters';
import TracePanel from '../../components/bots/TracePanel';
import ExplainPanel from '../../components/bots/ExplainPanel';

const timeframes = [
    { label: '1M', value: 'M1' },
    { label: '5M', value: 'M5' },
    { label: '15M', value: 'M15' },
    { label: '30M', value: 'M30' },
    { label: '1H', value: 'H1' },
    { label: '4H', value: 'H4' },
    { label: '1D', value: 'D1' },
    { label: '1W', value: 'W1' },
    { label: '1MN', value: 'MN1' },
];

const BacktestRunDetailPage = () => {
  const { runId } = useParams();
  const [runDetails, setRunDetails] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState(null);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [activeIndicators, setActiveIndicators] = useState([]);

  // State for trade log table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'entry_timestamp', direction: 'descending' });
  const [filters, setFilters] = useState({ direction: 'all', closure_reason: 'all' });
  const [selectedTrade, setSelectedTrade] = useState(null);
  // Trade filter state
  const [filterForm, setFilterForm] = useState({
    timeframe: null,
    apply_to: 'entry',
    logic: 'AND',
    filter_mode: 'INCLUDE',
    include_stats: true,
    direction: undefined, // legacy exclusive direction filter (takes precedence if set)
    apply_conditions_to_directions: undefined, // e.g. ['BUY'] or ['SELL'] when scoping conditions
    include_breakdown: false, // request server breakdown for scoped filtering
    conditions: []
  });

  // Load persisted filter form (scoped to run) once runId available
  useEffect(() => {
    if (!runId) return;
    try {
      const raw = localStorage.getItem(`bt_filter_${runId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Basic shape guard
        if (parsed && typeof parsed === 'object') {
          setFilterForm(f => ({ ...f, ...parsed }));
        }
      }
    } catch { /* ignore */ }
  }, [runId]);

  // Persist filter form (debounced minimal) when it changes
  const persistTimeoutRef = useRef(null);
  useEffect(() => {
    if (!runId) return;
    if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    persistTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(`bt_filter_${runId}`, JSON.stringify(filterForm));
      } catch { /* noop */ }
    }, 400);
    return () => {
      if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
    };
  }, [filterForm, runId]);
  const [filteredTradesResp, setFilteredTradesResp] = useState(null); // { items, count }
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [showFilteredOnly, setShowFilteredOnly] = useState(false);
  // Collapsible fill-level stats toggle (must come before any early returns to keep hook order stable)
  const [showFillStats, setShowFillStats] = useState(false);

  // Explain/Trace state
  const [activeTab, setActiveTab] = useState('Explain'); // 'Explain' | 'Trace'
  const [selectedBarIndex, setSelectedBarIndex] = useState(undefined);

  // Trace state
  const [traceParams, setTraceParams] = useState({ bar_index: undefined, ts: undefined, section: undefined, kind: undefined, limit: 200, offset: 0 });
  const [traceItems, setTraceItems] = useState([]);
  const [traceCount, setTraceCount] = useState(0);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState(null);
  // Track expanded trades (for showing TP plan details)
  const [expandedTrades, setExpandedTrades] = useState(new Set());
  const toggleExpandTrade = (id) => {
    if (!id) return;
    setExpandedTrades(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Explain state
  const [explainData, setExplainData] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState(null);
  // UI toggle for showing partial TP markers
  const [showPartialTps, setShowPartialTps] = useState(false);
  const explainAbortRef = useRef(null);
  const explainDebounceRef = useRef(null);

  const { filteredAndSortedTrades, totalPages, closureReasonOptions } = useMemo(() => {
    // Use filtered trades when available, else fall back to run's trades
    const filteredBase = (filteredTradesResp?.items && Array.isArray(filteredTradesResp.items)) ? filteredTradesResp.items : [];
    const baseTrades = showFilteredOnly ? filteredBase : (runDetails?.simulated_trades_log || []);
    // Deduplicate by id; keep the "latest" occurrence (preferring one with realized_pnl, exit_timestamp or CLOSED status)
    const idMap = new Map();
    for (const t of baseTrades) {
      if (!t || !t.id) {
        // Push anonymous trades directly later
        const arr = idMap.get('__anon__') || [];
        arr.push(t);
        idMap.set('__anon__', arr);
        continue;
      }
      if (!idMap.has(t.id)) {
        idMap.set(t.id, t);
      } else {
        const existing = idMap.get(t.id);
        // Heuristic: choose trade with realized_pnl defined, else with exit_timestamp, else with CLOSED status, else latest exit_timestamp chronologically
        const score = (tr) => {
          let s = 0;
            if (typeof tr.realized_pnl === 'number') s += 4;
            if (tr.exit_timestamp) s += 2;
            if (String(tr.status).toUpperCase() === 'CLOSED') s += 1;
            // fresher exit timestamp adds fractional
            if (tr.exit_timestamp) s += (new Date(tr.exit_timestamp).getTime() / 1e13); // keep tiny
          return s;
        };
        if (score(t) >= score(existing)) {
          idMap.set(t.id, t);
        }
      }
    }
    const allTrades = [];
    for (const [k,v] of idMap.entries()) {
      if (k === '__anon__') { allTrades.push(...v); } else { allTrades.push(v); }
    }

    const closureReasons = ['all', ...new Set(allTrades.map(t => t.closure_reason).filter(Boolean))];

    const filtered = allTrades.filter(trade => {
      const directionMatch = filters.direction === 'all' || trade.direction.toLowerCase() === filters.direction;
      const reasonMatch = filters.closure_reason === 'all' || trade.closure_reason === filters.closure_reason;
      return directionMatch && reasonMatch;
    });

    const sorted = [...filtered].sort((a, b) => {
      const key = sortConfig.key;
      const dir = sortConfig.direction === 'ascending' ? 1 : -1;
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last
      if (bv == null) return -1;
      // Date fields
      if (key.endsWith('timestamp') || key.includes('time')) {
        const ad = new Date(av).getTime();
        const bd = new Date(bv).getTime();
        if (ad === bd) return 0;
        return ad < bd ? -1 * dir : 1 * dir;
      }
      // Numeric compare when both numeric
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -1 * dir : 1 * dir;
      }
      // Fallback string compare (case-insensitive)
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as === bs) return 0;
      return as < bs ? -1 * dir : 1 * dir;
    });
    
    const pages = Math.ceil(sorted.length / itemsPerPage);
    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return {
      filteredAndSortedTrades: paginated,
      totalPages: pages,
      closureReasonOptions: closureReasons,
    };
  }, [runDetails?.simulated_trades_log, filteredTradesResp?.items, currentPage, itemsPerPage, sortConfig, filters, showFilteredOnly]);

  // Metrics for filtered trades (computed client-side when results are present)
  const filteredMetrics = useMemo(() => {
    const items = filteredTradesResp?.items;
    if (!Array.isArray(items) || items.length === 0) return null;
    const n = items.length;
    let wins = 0, losses = 0;
    let sum = 0, grossWin = 0, grossLoss = 0;
    for (const t of items) {
      const pnl = (t?.realized_pnl ?? t?.pnl ?? 0) || 0;
      sum += pnl;
      if (pnl > 0) { wins += 1; grossWin += pnl; }
      else if (pnl < 0) { losses += 1; grossLoss += -pnl; }
    }
    const avg = n ? sum / n : 0;
    const winRate = n ? wins / n : 0;
    const profitFactor = grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0);
    return { count: n, wins, losses, avg, sum, winRate, profitFactor };
  }, [filteredTradesResp?.items]);

  // Helpers for filter UI
  const addCondition = () => {
    // Default: EMA(21) > EMA(50), using registry names expected by BE
  const defaultIndicator = (len) => ({ type: 'indicator', name: 'EMAIndicator', output: 'ema', params: { length: len, src: 'close' }, input: { type: 'price', source: 'close' } });
    setFilterForm(f => ({
      ...f,
      conditions: [
        ...(f.conditions || []),
        { left: defaultIndicator(21), op: '>', right: defaultIndicator(50) },
      ],
    }));
  };
  const updateCondition = (idx, patch) => {
    setFilterForm(f => ({ ...f, conditions: f.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  };
  const removeCondition = (idx) => {
    setFilterForm(f => ({ ...f, conditions: f.conditions.filter((_, i) => i !== idx) }));
  };
  const clearFilterResults = () => { setFilteredTradesResp(null); setFilterError(null); };
  const submitFilter = async (overridePayload) => {
    setFilterLoading(true); setFilterError(null);
    try {
      const ff = overridePayload || filterForm;

      // Transform a potentially rich IndicatorSelector operand into BE expected minimal shape
      const normalizeOperand = (op) => {
        if (!op || typeof op !== 'object') return op;
        if (op.type === 'price') {
          return { type: 'price', field: op.field || op.source || 'close' };
        }
        if (op.type === 'number') {
          return { type: 'number', value: Number(op.value) };
        }
        if (op.type === 'indicator') {
          const name = op.name && typeof op.name === 'string'
            ? op.name
            : 'EMAIndicator';
          const params = { ...(op.params || {}) };
          // If input.price was used, map to params.src for BE
          if (!params.src && op.input && op.input.type === 'price' && op.input.source) {
            params.src = op.input.source;
          }
          if (!params.src) params.src = 'close';
          // Ensure common numeric params are numbers
          if (params.length !== undefined) params.length = Number(params.length);
          if (params.std !== undefined) params.std = Number(params.std);
          const out = {
            type: 'indicator',
            name,
            params,
            output: op.output || 'default',
          };
          return out;
        }
        return op;
      };

      // Build direction scoping semantics per new feature spec
      // Precedence: if direction provided -> legacy path (ignore scoped list)
      let scoped = Array.isArray(ff.apply_conditions_to_directions) ? ff.apply_conditions_to_directions.filter(d => d === 'BUY' || d === 'SELL') : undefined;
      if (scoped && scoped.length !== 1) {
        // If 0 or 2 directions selected treat as undefined (global)
        scoped = undefined;
      }
      const payload = {
        apply_to: ff.apply_to || 'entry',
        logic: ff.logic || 'AND',
        filter_mode: ff.filter_mode || 'INCLUDE',
        include_stats: !!ff.include_stats,
        // direction only if explicitly set (legacy). If set, do NOT send scoped list.
        direction: ff.direction || undefined,
        ...(ff.direction ? {} : (scoped ? { apply_conditions_to_directions: scoped } : {})),
        ...(ff.direction ? {} : (scoped && ff.include_breakdown ? { include_breakdown: true } : {})),
        conditions: Array.isArray(ff.conditions)
          ? ff.conditions.map(c => ({
              left: normalizeOperand(c.left),
              op: c.op || '>',
              right: normalizeOperand(c.right),
            }))
          : [],
      };
      // Only include timeframe if user explicitly selected a different timeframe.
      const baseRunTimeframe = runDetails?.original_timeframe || runDetails?.run_timeframe || runDetails?.timeframe;
      if (ff.timeframe && ff.timeframe !== baseRunTimeframe) {
        payload.timeframe = ff.timeframe;
      }
      const res = await filterBacktestTrades(runId, payload);
      setFilteredTradesResp(res);
      setCurrentPage(1);
    } catch (e) {
      setFilterError(e?.message || 'Failed to filter trades');
    } finally {
      setFilterLoading(false);
    }
  };

  const loadPreset = async (preset) => {
    if (!preset) return;
    const payload = preset.payload || preset;
    const nextForm = {
      timeframe: payload.timeframe ?? null,
      apply_to: payload.apply_to || 'entry',
      logic: payload.logic || 'AND',
      filter_mode: payload.filter_mode || 'INCLUDE',
      include_stats: payload.include_stats !== undefined ? !!payload.include_stats : true,
      direction: payload.direction,
      apply_conditions_to_directions: payload.apply_conditions_to_directions,
      include_breakdown: !!payload.include_breakdown,
      conditions: Array.isArray(payload.conditions) ? payload.conditions : [],
    };
    setFilterForm(nextForm);
    await submitFilter(nextForm);
  };

  const savePreset = async () => {
    const name = window.prompt('Preset name');
    if (!name) return;
    setIsSavingPreset(true);
    try {
      const body = {
        name,
        payload: {
          timeframe: filterForm.timeframe || undefined,
          apply_to: filterForm.apply_to || 'entry',
            logic: filterForm.logic || 'AND',
          filter_mode: filterForm.filter_mode || 'INCLUDE',
          include_stats: !!filterForm.include_stats,
          direction: filterForm.direction || undefined,
          apply_conditions_to_directions: filterForm.apply_conditions_to_directions,
          include_breakdown: filterForm.include_breakdown || undefined,
          conditions: Array.isArray(filterForm.conditions) ? filterForm.conditions : [],
        },
      };
      await createFilterPreset(body);
      const fresh = await listFilterPresets({});
      setPresets(fresh?.items || fresh || []);
    } catch (e) {
      setFilterError(e?.message || 'Failed to save preset');
    } finally {
      setIsSavingPreset(false);
    }
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Effect 1: Fetch initial run details and available indicators
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [details, indicators, presetsResp] = await Promise.all([
          getBacktestRun(runId),
          fetchAvailableIndicators(),
          listFilterPresets({}).catch(() => ([])),
        ]);
  setRunDetails(details);
  // Prefer explicit original_timeframe then new run_timeframe then legacy timeframe
  const initialTf = details.original_timeframe || details.run_timeframe || details.timeframe || null;
  setSelectedTimeframe(initialTf);
        setAvailableIndicators(indicators || []);
        const presetItems = presetsResp?.items || presetsResp || [];
        setPresets(presetItems);
      } catch (err) {
        setError(err.message || `Failed to fetch initial data for run ${runId}.`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [runId]);

  // Effect 2: Poll for status updates if the run is in progress
  useEffect(() => {
    if (!runDetails || (runDetails.status !== 'PENDING' && runDetails.status !== 'RUNNING')) {
      return; // Don't poll if not pending or running
    }

    const intervalId = setInterval(() => {
      getBacktestRun(runId)
        .then(data => setRunDetails(data)) // Just update the details
        .catch(err => {
          console.error("Error polling for run status:", err);
          clearInterval(intervalId); // Stop on error
        });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [runDetails, runId]);

  // Effect 3: Fetch chart data whenever conditions are met
  useEffect(() => {
    const normalizeIndicatorData = (indData) => {
      if (!indData || typeof indData !== 'object') return indData;
      const out = {};
      for (const [k, v] of Object.entries(indData)) {
        if (!v || typeof v !== 'object') { out[k] = v; continue; }
        const pane = typeof v.pane_type === 'string' ? v.pane_type.toLowerCase() : v.pane_type;
        out[k] = { ...v, pane_type: pane };
      }
      return out;
    };
    const fetchChartData = async () => {
      // Only fetch if the run is completed
      if (runDetails?.status !== 'COMPLETED') {
        return;
      }
      
      try {
  const data = await getBacktestRunChartData(runId, selectedTimeframe ? { timeframe: selectedTimeframe } : {});
        const normalized = {
          ...data,
          indicator_data: normalizeIndicatorData(data?.indicator_data),
        };
        setChartData(normalized);
      } catch (err) {
        console.error("Failed to fetch chart data:", err);
        setChartData(null); // Clear old data on error
      }
    };

    fetchChartData();
  }, [runId, runDetails?.status, selectedTimeframe]); // Re-fetch when status or timeframe changes

  const handleTimeframeChange = (newTimeframe) => {
    if (!newTimeframe) return;
    setSelectedTimeframe(newTimeframe);
    // fetch effect will respond to selectedTimeframe
  };

  const handleAddIndicator = async (arg1, arg2, paneType, scaleType, timeframeOverride) => {
    // Overload: either (name, params, ...) or (nestedSpecObject)
    const isNestedSpec = arg1 && typeof arg1 === 'object' && arg1.name && Array.isArray(arg1.inputs);
    if (!isNestedSpec) {
      const name = arg1; const params = arg2;
      const newIndicator = { name, params, paneType };
      const exists = activeIndicators.some(ind => ind.name.toLowerCase() === String(name).toLowerCase() && JSON.stringify(ind.params) === JSON.stringify(params));
      if (!exists) setActiveIndicators(prev => [...prev, newIndicator]);
      try {
        const payload = [{ name, params }];
        const res = await addBacktestIndicators(runId, payload, timeframeOverride ? { timeframe: timeframeOverride } : (selectedTimeframe ? { timeframe: selectedTimeframe } : {}));
        const raw = res?.indicator_data || res?.data?.indicator_data || {};
        const indicatorData = (() => {
          const out = {};
          for (const [k, v] of Object.entries(raw)) {
            const pane = typeof v?.pane_type === 'string' ? v.pane_type.toLowerCase() : v?.pane_type;
            out[k] = { ...v, pane_type: pane };
          }
          return out;
        })();
        if (indicatorData && typeof indicatorData === 'object') {
          setChartData(prev => ({ ...(prev || {}), indicator_data: { ...((prev && prev.indicator_data) || {}), ...indicatorData } }));
        }
      } catch (e) {
        console.error('Failed to add backtest indicator:', e);
        setError(typeof e === 'string' ? e : (e?.message || 'Failed to add indicator'));
      }
      return;
    }

    // Nested spec path
    const nestedSpec = arg1;
    try {
      const childName = nestedSpec?.name || 'spec';
      const firstInput = Array.isArray(nestedSpec?.inputs) ? nestedSpec.inputs[0] : null;
      const parentName = (firstInput && (firstInput.name || firstInput.ref)) ? (firstInput.name || firstInput.ref) : 'price';
      // Encode parent in alias so FE can map overlays to parent's pane: <child>_src_<parent>
      const aliasBase = `${String(childName)}_src_${String(parentName)}`;
      const body = { specs: [ { alias: aliasBase, spec: nestedSpec } ] };
      const paramsObj = timeframeOverride ? { timeframe: timeframeOverride } : (selectedTimeframe ? { timeframe: selectedTimeframe } : {});
      const res = await addBacktestIndicators(runId, body, paramsObj);
      const raw = res?.indicator_data || res?.data?.indicator_data || {};
      const indicatorData = (() => {
        const out = {};
        for (const [k, v] of Object.entries(raw)) {
          const pane = typeof v?.pane_type === 'string' ? v.pane_type.toLowerCase() : v?.pane_type;
          out[k] = { ...v, pane_type: pane };
        }
        return out;
      })();
      if (indicatorData && typeof indicatorData === 'object') {
        setChartData(prev => ({ ...(prev || {}), indicator_data: { ...((prev && prev.indicator_data) || {}), ...indicatorData } }));
      }
    } catch (e) {
      console.error('Failed to add nested spec indicator:', e);
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to add nested spec indicator'));
    }
  };

  const handleRemoveIndicator = (indicatorId) => {
    // In backtest mode, the unique ID is generated on the frontend.
    // Here, we assume the `IndicatorControls` component passes back the `name` as the ID.
    setActiveIndicators(prev => prev.filter(ind => ind.name !== indicatorId));
  };

  // Fetch Explain when selectedBarIndex changes (debounced + cancellable)
  useEffect(() => {
    // cleanup previous debounce and request
    if (explainDebounceRef.current) {
      clearTimeout(explainDebounceRef.current);
      explainDebounceRef.current = null;
    }
    if (explainAbortRef.current) {
      explainAbortRef.current.abort();
      explainAbortRef.current = null;
    }

    if (selectedBarIndex === undefined || selectedBarIndex === null) {
      setExplainData(null);
      setExplainError(null);
      return;
    }

    setExplainLoading(true);
    setExplainError(null);

    explainDebounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      explainAbortRef.current = controller;
      getBacktestExplain(runId, { bar_index: selectedBarIndex }, { signal: controller.signal })
        .then((data) => setExplainData(data))
        .catch((err) => {
          const msg = String(err?.message || '');
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || msg.toLowerCase().includes('canceled') || msg.toLowerCase().includes('abort')) {
            return; // ignore cancellation
          }
          setExplainError(err?.message || 'Failed to load explain');
        })
        .finally(() => {
          setExplainLoading(false);
          explainAbortRef.current = null;
        });
    }, 200);

    return () => {
      if (explainDebounceRef.current) {
        clearTimeout(explainDebounceRef.current);
        explainDebounceRef.current = null;
      }
      if (explainAbortRef.current) {
        explainAbortRef.current.abort();
        explainAbortRef.current = null;
      }
    };
  }, [selectedBarIndex, runId]);

  // Auto-load Trace when switching to Trace tab (debounced)
  useEffect(() => {
    if (activeTab !== 'Trace') return;
    const bar = (traceParams?.bar_index ?? selectedBarIndex);
    if (bar === undefined && traceParams?.ts === undefined) return;
    const next = { ...traceParams, offset: 0, bar_index: bar };
    setTraceParams(next);
    fetchTrace(next, { append: false, debounceMs: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-refresh Trace when selectedBarIndex changes while on Trace tab (debounced)
  useEffect(() => {
    if (activeTab !== 'Trace') return;
    if (selectedBarIndex === undefined || selectedBarIndex === null) return;
    const next = { ...traceParams, offset: 0, bar_index: selectedBarIndex };
    setTraceParams(next);
    fetchTrace(next, { append: false, debounceMs: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarIndex, activeTab]);

  const fetchTrace = useCallback((params, { append, debounceMs } = { append: false, debounceMs: 0 }) => {
    // local refs for cancellation and debounce
    if (!fetchTrace.traceAbortRef) fetchTrace.traceAbortRef = { current: null };
    if (!fetchTrace.traceDebounceRef) fetchTrace.traceDebounceRef = { current: null };

    // clear pending debounce
    if (fetchTrace.traceDebounceRef.current) {
      clearTimeout(fetchTrace.traceDebounceRef.current);
      fetchTrace.traceDebounceRef.current = null;
    }
    // cancel in-flight
    if (fetchTrace.traceAbortRef.current) {
      fetchTrace.traceAbortRef.current.abort();
      fetchTrace.traceAbortRef.current = null;
    }

    const run = () => {
      const controller = new AbortController();
      fetchTrace.traceAbortRef.current = controller;
      setTraceLoading(true);
      setTraceError(null);
      getBacktestTrace(runId, params, { signal: controller.signal })
        .then((res) => {
          setTraceCount(res?.count ?? 0);
          setTraceItems((prev) => (append ? [...prev, ...(res?.items || [])] : (res?.items || [])));
        })
        .catch((err) => {
          const msg = String(err?.message || '');
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || msg.toLowerCase().includes('canceled') || msg.toLowerCase().includes('abort')) {
            return; // ignore cancellation
          }
          setTraceError(err?.message || 'Failed to load trace');
        })
        .finally(() => {
          setTraceLoading(false);
          fetchTrace.traceAbortRef.current = null;
        });
    };

    if (debounceMs && debounceMs > 0) {
      fetchTrace.traceDebounceRef.current = setTimeout(run, debounceMs);
    } else {
      run();
    }
  }, [runId]);

  const handleApplyTraceFilters = () => {
    const next = {
      ...traceParams,
      offset: 0,
      bar_index: traceParams.bar_index ?? (selectedBarIndex ?? undefined),
    };
    setTraceParams(next);
    fetchTrace(next, { append: false });
  };

  const handleLoadMoreTrace = () => {
    const next = { ...traceParams, offset: (traceItems?.length || 0) };
    setTraceParams(next);
    fetchTrace(next, { append: true });
  };

  const handleClearTraceFilters = () => {
    const cleared = { bar_index: selectedBarIndex ?? undefined, ts: undefined, section: undefined, kind: undefined, limit: traceParams.limit || 200, offset: 0 };
    setTraceParams(cleared);
    // optional immediate refetch if a bar is available
    if (cleared.bar_index !== undefined || cleared.ts !== undefined) {
      fetchTrace(cleared, { append: false, debounceMs: 0 });
    } else {
      setTraceItems([]);
      setTraceCount(0);
    }
  };

  if (isLoading && !runDetails) {
    return (
      <PageLayout
        title="Loading Backtest Run..."
        fullWidth
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p>Loading details...</p>
      </PageLayout>
    );
  }
  if (error && !runDetails) {
    return (
      <PageLayout
        title="Error"
        fullWidth
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p className="text-destructive">{error}</p>
      </PageLayout>
    );
  }
  if (!runDetails) {
    return (
      <PageLayout
        title="Backtest Run Not Found"
        fullWidth
        backButton={{ label: 'Back to Bots', to: '/bots', preferHistory: false, hideLabelOnMobile: true }}
      >
        <p>The requested backtest run could not be found.</p>
      </PageLayout>
    );
  }

  const pageTitle = runDetails?.description ? runDetails.description : 'Backtest Run';
  const stats = runDetails?.stats || {};

  // Derive closure reason counts (SL_HIT vs EXIT)
  const closureReasonSummary = (() => {
    const trades = runDetails?.simulated_trades_log || [];
    const total = trades.length;
    let slHit = 0;
    let exit = 0;
    for (const t of trades) {
      const r = String(t.closure_reason || '').toUpperCase();
      if (r === 'SL_HIT') slHit += 1;
      else if (r === 'EXIT') exit += 1; // adjust if backend uses a different literal
    }
    const pct = (n) => total ? ((n / total) * 100).toFixed(1) + '%' : 'N/A';
    return { total, slHit, exit, slHitPct: pct(slHit), exitPct: pct(exit) };
  })();

  // Derive partial TP hit rates (per tp_plan tag) by scanning OHLCV bars for actual price touches
  const partialTpHitRates = (() => {
    const bars = chartData?.ohlcv_data || [];
    if (!bars.length) return [];
    // Pre-normalize bars to ms + keep high/low
    const nb = bars.map(b => ({
      timeMs: b.time > 1000000000000 ? b.time : b.time * 1000,
      high: b.high,
      low: b.low,
    }));
    const acc = {}; // tag -> { hits, total }
    for (const trade of (runDetails?.simulated_trades_log || [])) {
      if (!Array.isArray(trade.tp_plan) || !trade.tp_plan.length) continue;
      const entryMs = trade.entry_timestamp ? new Date(trade.entry_timestamp).getTime() : undefined;
      const exitMs = trade.exit_timestamp ? new Date(trade.exit_timestamp).getTime() : undefined;
      if (!entryMs) continue;
      const dir = String(trade.direction || '').toUpperCase();
      for (const tp of trade.tp_plan) {
        if (!tp || !tp.tag || typeof tp.price !== 'number') continue;
        const tag = String(tp.tag).toLowerCase();
        if (!acc[tag]) acc[tag] = { hits: 0, total: 0 };
        acc[tag].total += 1;
        // Find if any bar after entry (inclusive) until exit (exclusive, unless exitMs absent) touches TP
        const hit = nb.some(bar => {
          if (bar.timeMs < entryMs) return false;
          if (exitMs && bar.timeMs > exitMs) return false;
          if (dir === 'BUY') return bar.high >= tp.price; // upward reach
          if (dir === 'SELL') return bar.low <= tp.price; // downward reach
          return false;
        });
        if (hit) acc[tag].hits += 1;
      }
    }
    const entries = Object.entries(acc).map(([tag, v]) => ({
      tag,
      hits: v.hits,
      total: v.total,
      rate: v.total ? v.hits / v.total : 0,
      sortKey: (() => { const m = tag.match(/(\d+)/); return m ? parseInt(m[1],10) : Number.MAX_SAFE_INTEGER; })()
    }));
    entries.sort((a,b) => a.sortKey - b.sortKey || a.tag.localeCompare(b.tag));
    return entries;
  })();

  // Pass the entire OHLCV data to the chart
  const ohlcvData = chartData?.ohlcv_data || [];
  // If you want to window indicators as well, you can add code here

  // Choose which trades to display on the chart: either all run trades or only filtered ones
  const chartTrades = (() => {
    const source = showFilteredOnly
      ? (filteredTradesResp?.items || [])
      : (runDetails?.simulated_trades_log || []);
    const list = Array.isArray(source) ? source.filter(Boolean) : [];
    if (!list.length) return [];
    // Deduplicate by id similar to table heuristic
    const idMap = new Map();
    for (const t of list) {
      if (!t || !t.id) {
        const arr = idMap.get('__anon__') || [];
        arr.push(t);
        idMap.set('__anon__', arr);
        continue;
      }
      const existing = idMap.get(t.id);
      if (!existing) idMap.set(t.id, t);
      else {
        const score = (tr) => {
          let s = 0;
          if (typeof tr?.realized_pnl === 'number') s += 4;
          if (tr?.exit_timestamp) s += 2;
          if (String(tr?.status || '').toUpperCase() === 'CLOSED') s += 1;
          if (tr?.exit_timestamp) s += (new Date(tr.exit_timestamp).getTime() / 1e13);
          return s;
        };
        if (score(t) >= score(existing)) idMap.set(t.id, t);
      }
    }
    const deduped = [];
    for (const [k, v] of idMap.entries()) {
      if (k === '__anon__') deduped.push(...v); else deduped.push(v);
    }
    // When scoped direction filtering is enabled and "show filtered only" is on,
    // hide unaffected directions on the chart (so it's visually obvious what's filtered)
    const scoped = (!filterForm.direction && Array.isArray(filterForm.apply_conditions_to_directions) && filterForm.apply_conditions_to_directions.length === 1)
      ? filterForm.apply_conditions_to_directions[0]
      : undefined;

    // Apply the same local direction/closure filters as the table, with optional scoped-hide
    return deduped.filter(trade => {
      const dir = String(trade?.direction || '').toLowerCase();
      // Normalize LONG/SHORT → BUY/SELL for scope compare
      const norm = dir === 'long' ? 'buy' : dir === 'short' ? 'sell' : dir;
      // If scoped, restrict to affected direction when showFilteredOnly
      const scopedOk = (showFilteredOnly && scoped) ? (norm === scoped.toLowerCase()) : true;
      const directionMatch = (filters.direction === 'all' || norm === String(filters.direction).toLowerCase()) && scopedOk;
      const reasonMatch = filters.closure_reason === 'all' || trade.closure_reason === filters.closure_reason;
      return directionMatch && reasonMatch;
    });
  })();

  // Base trade markers (entries/exits); convert ISO timestamps to epoch ms
  const tradeMarkers = chartTrades.map(t => {
    const entryMs = t.entry_timestamp ? new Date(t.entry_timestamp).getTime() : undefined;
    const exitMs = t.exit_timestamp ? new Date(t.exit_timestamp).getTime() : undefined;
    const pnlNum = (typeof t.realized_pnl === 'number') ? t.realized_pnl : (typeof t.pnl === 'number' ? t.pnl : 0);
    return {
      ...t,
      // ensure the TradingChart has consistent fields
      pnl: pnlNum,
      entry_timestamp: entryMs,
      exit_timestamp: exitMs,
    };
  });

  // Compute partial TP markers (first bar where TP price would have been hit)
  const partialTpMarkers = (() => {
    if (!showPartialTps) return [];
    const bars = chartData?.ohlcv_data || [];
    if (!bars.length) return [];
    // Normalize bar times to ms for comparison
    const normalizedBars = bars.map(b => ({
      ...b,
      _timeMs: b.time > 1000000000000 ? b.time : b.time * 1000,
    }));
    const out = [];
  for (const trade of chartTrades || []) {
      if (!Array.isArray(trade.tp_plan) || !trade.tp_plan.length) continue;
      const entryMs = trade.entry_timestamp ? new Date(trade.entry_timestamp).getTime() : undefined;
      const exitMs = trade.exit_timestamp ? new Date(trade.exit_timestamp).getTime() : undefined;
      if (!entryMs) continue;
      const dir = String(trade.direction || '').toUpperCase();
      for (const tp of trade.tp_plan) {
        if (typeof tp.price !== 'number') continue;
        // Find first bar after entry (inclusive) and before exit (if any) hitting TP
        const hitBar = normalizedBars.find(b => {
          if (b._timeMs < entryMs) return false;
          if (exitMs && b._timeMs > exitMs) return false;
          if (dir === 'BUY') return b.high >= tp.price; // price went up enough
          if (dir === 'SELL') return b.low <= tp.price;  // price went down enough
          return false;
        });
        if (!hitBar) continue;
        const timeSeconds = hitBar.time > 1000000000000 ? Math.floor(hitBar.time / 1000) : hitBar.time; // original scale if already seconds
        out.push({
          time: timeSeconds,
          position: dir === 'BUY' ? 'aboveBar' : 'belowBar',
          color: tp.status === 'FILLED' ? '#10b981' : '#fbbf24',
          shape: 'circle',
          // label should not include price per UX request
          text: `${tp.tag || 'TP'}`,
          _groupKey: `${timeSeconds}|${tp.tag || 'TP'}`,
        });
      }
    }
    // Aggregate duplicates (same time, tag, price) -> single marker with count suffix
    const grouped = new Map();
    for (const m of out) {
      const k = m._groupKey;
      if (!grouped.has(k)) grouped.set(k, { ...m, count: 1 });
      else grouped.get(k).count += 1;
    }
    const aggregated = Array.from(grouped.values()).map(m => {
      if (m.count > 1) {
        return { ...m, text: `${m.text} (x${m.count})` };
      }
      return m;
    });
    return aggregated
      .sort((a, b) => a.time - b.time)
      .map(m => { const { _groupKey, ...rest } = m; return rest; });
  })();

  // Formatting helpers for stats
  const fmtNum = (v, digits = 2) => (typeof v === 'number' && isFinite(v) ? v.toFixed(digits) : 'N/A');
  const n = (v) => fmtNum(v, 2);
  const n2 = (v) => fmtNum(v, 2);
  const int = (v) => (typeof v === 'number' && isFinite(v) ? Math.round(v) : (v === 0 ? 0 : 'N/A'));
  const pct = (v) => (typeof v === 'number' && isFinite(v) ? (v * 100).toFixed(2) + '%' : 'N/A');

  const equityCurveData = runDetails?.equity_curve || [];

  // Manually compute sum of P&L across main positions (dedup by id; prefer CLOSED/realized rows)
  const calculatedPositionsPnl = (() => {
    const trades = Array.isArray(runDetails?.simulated_trades_log) ? runDetails.simulated_trades_log.filter(Boolean) : [];
    if (!trades.length) return 0;
    const idMap = new Map();
    const anon = [];
    const score = (tr) => {
      let s = 0;
      if (typeof tr?.realized_pnl === 'number') s += 4;
      if (tr?.exit_timestamp) s += 2;
      if (String(tr?.status || '').toUpperCase() === 'CLOSED') s += 1;
      if (tr?.exit_timestamp) {
        const t = new Date(tr.exit_timestamp).getTime();
        if (Number.isFinite(t)) s += (t / 1e13);
      }
      return s;
    };
    for (const t of trades) {
      const id = t?.id;
      if (!id) { anon.push(t); continue; }
      const existing = idMap.get(id);
      if (!existing || score(t) >= score(existing)) idMap.set(id, t);
    }
    const unique = [...idMap.values(), ...anon];
    let sum = 0;
    for (const t of unique) {
      const raw = (typeof t?.realized_pnl === 'number' ? t.realized_pnl : (typeof t?.pnl === 'number' ? t.pnl : parseFloat(t?.realized_pnl ?? t?.pnl)));
      const pnl = Number.isFinite(raw) ? raw : NaN;
      if (Number.isFinite(pnl)) sum += pnl;
    }
    return sum;
  })();

  return (
    <PageLayout
      title={pageTitle}
      fullWidth
      backButton={{ label: 'Back to Version', to: `/bots/${runDetails.bot_id}/versions/${runDetails.bot_version}`, preferHistory: false, hideLabelOnMobile: true }}
    >
      {error && <p className="text-destructive mb-4">Error updating run details: {error}</p>}
      <div className="space-y-6">
        <section>
          <h2 className="text-xl font-semibold text-textPrimary mb-2">Run Overview</h2>
          <div className="bg-backgroundSurface p-4 rounded-lg shadow grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            <p><strong>Status:</strong> {runDetails.status} { (runDetails.status === 'PENDING' || runDetails.status === 'RUNNING') && "(auto-refreshing)"}</p>
            <p><strong>Bot Name:</strong> {runDetails.bot_name || 'N/A'}</p>
            <p><strong>Bot Version:</strong> <Link to={`/bots/${runDetails.bot_id}/versions/${runDetails.bot_version}`} className="text-primary hover:underline">{runDetails.bot_version_name || 'N/A'}</Link></p>
            <p><strong>Config Label:</strong> {runDetails.config_label || 'N/A'}</p>
            {runDetails.description && <p><strong>Description:</strong> {runDetails.description}</p>}
            <p><strong>Instrument:</strong> {runDetails.instrument_symbol || 'N/A'}</p>
            <p><strong>Data Window:</strong> {new Date(runDetails.data_window_start).toLocaleString()} to {new Date(runDetails.data_window_end).toLocaleString()}</p>
            <p><strong>Created At:</strong> {new Date(runDetails.created_at).toLocaleString()}</p>
            {runDetails.started_at && <p><strong>Started At:</strong> {new Date(runDetails.started_at).toLocaleString()}</p>}
            {runDetails.completed_at && <p><strong>Completed At:</strong> {new Date(runDetails.completed_at).toLocaleString()}</p>}
            {runDetails.error_message && <p className="text-destructive md:col-span-2"><strong>Error:</strong> {runDetails.error_message}</p>}
            {stats.error && runDetails.status === 'FAILED' && <p className="text-destructive md:col-span-2"><strong>Failure Reason:</strong> {stats.error}</p>}
          </div>
        </section>

        {(runDetails.status === 'COMPLETED' || runDetails.status === 'FAILED') && stats && Object.keys(stats).length > 0 && (
          <section>
            <h2 className="text-xl font-semibold text-textPrimary mb-2">Performance Stats</h2>
            <div className="bg-backgroundSurface p-4 rounded-lg shadow space-y-4 text-xs md:text-sm">
              {/* Core KPIs grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-2">
                <p><strong>Net P&L:</strong> {n(stats.net_pnl)}</p>
                <p><strong>Calculated P/L:</strong> {n(calculatedPositionsPnl)}</p>
                <p><strong>Return %:</strong> {pct(stats.total_return_pct)}</p>
                <p><strong>Final Equity:</strong> {n(stats.final_equity)}</p>
                <p><strong>Initial Equity:</strong> {n(stats.initial_equity)}</p>
                <p><strong>Total Spread Cost:</strong> {n(stats.total_spread_cost)}</p>
                <p><strong>Total Commission Paid:</strong> {n(stats.total_commission_paid)}</p>
                <p><strong>Max Drawdown:</strong> {n(stats.max_drawdown)}</p>
                <p><strong>Max DD %:</strong> {pct(stats.max_drawdown_pct)}</p>
                <p><strong>Sharpe:</strong> {n2(stats.sharpe_ratio)}</p>
                <p><strong>Sortino:</strong> {n2(stats.sortino_ratio)}</p>
                <p><strong>Calmar:</strong> {n2(stats.calmar_ratio)}</p>
                <p><strong>Total Trades:</strong> {int(stats.total_trades)}</p>
                <p><strong>Win Rate (pos):</strong> {pct(stats.win_rate)}</p>
                <p><strong>Profit Factor (pos):</strong> {n2(stats.profit_factor)}</p>
                <p><strong>Expectancy (pos):</strong> {n(stats.expectancy)}</p>
                <p><strong>Avg Win (pos):</strong> {n(stats.average_win)}</p>
                <p><strong>Avg Loss (pos):</strong> {n(stats.average_loss)}</p>
                <p><strong>Biggest Win (pos):</strong> {n(stats.biggest_win_position)}</p>
                <p><strong>Biggest Loss (pos):</strong> {n(stats.biggest_loss_position)}</p>
                <p><strong>Long Trades:</strong> {int(stats.long_trades_count)}</p>
                <p><strong>Short Trades:</strong> {int(stats.short_trades_count)}</p>
                <p><strong>SL Hit Trades:</strong> {closureReasonSummary.total ? `${closureReasonSummary.slHit} (${closureReasonSummary.slHitPct})` : 'N/A'}</p>
                <p><strong>Exit Trades:</strong> {closureReasonSummary.total ? `${closureReasonSummary.exit} (${closureReasonSummary.exitPct})` : 'N/A'}</p>
                {partialTpHitRates.map(tp => (
                  <p key={tp.tag}><strong>{tp.tag.toUpperCase()} Hit Rate:</strong> {tp.total ? (tp.rate * 100).toFixed(1) + '%' : 'N/A'} ({tp.hits}/{tp.total})</p>
                ))}
                <p><strong>Max Cons Wins (pos):</strong> {int(stats.max_consecutive_wins_positions)}</p>
                <p><strong>Max Cons Losses (pos):</strong> {int(stats.max_consecutive_losses_positions)}</p>
              </div>

              {/* Collapsible Fill-level stats */}
              <div className="border-t pt-3">
                <button
                  type="button"
                  onClick={() => setShowFillStats(s => !s)}
                  className="text-primary text-xs font-semibold flex items-center gap-1"
                >
                  {showFillStats ? '▼' : '▶'} Fill-Level Metrics
                </button>
                {showFillStats && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-2">
                    <p><strong>Total Fills:</strong> {int(stats.total_fills)}</p>
                    <p><strong>Win Rate (fills):</strong> {pct(stats.win_rate_fills)}</p>
                    <p><strong>Profit Factor (fills):</strong> {n2(stats.profit_factor_fills)}</p>
                    <p><strong>Expectancy (fills):</strong> {n(stats.expectancy_fills)}</p>
                    <p><strong>Avg Win (fill):</strong> {n(stats.average_win_fill)}</p>
                    <p><strong>Avg Loss (fill):</strong> {n(stats.average_loss_fill)}</p>
                    <p><strong>Biggest Win (fill):</strong> {n(stats.biggest_win_fill)}</p>
                    <p><strong>Biggest Loss (fill):</strong> {n(stats.biggest_loss_fill)}</p>
                    <p><strong>Max Cons Wins (fills):</strong> {int(stats.max_consecutive_wins)}</p>
                    <p><strong>Max Cons Losses (fills):</strong> {int(stats.max_consecutive_losses)}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {runDetails.status === 'COMPLETED' && Array.isArray(equityCurveData) && equityCurveData.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold text-textPrimary mb-2">Equity Curve</h2>
            <div className="bg-backgroundSurface p-4 rounded-lg shadow">
              <EquityCurveInline data={equityCurveData} />
            </div>
          </section>
        )}

        {runDetails.status === 'COMPLETED' && (
          <section>
            <h2 className="text-xl font-semibold text-textPrimary mb-2">Backtest Chart</h2>
            <div className="bg-backgroundSurface p-4 rounded-lg shadow">
              <div className="flex items-center space-x-4 mb-4">
                  <div>
                      <label htmlFor="timeframe-select" className="mr-2 font-semibold text-textPrimary">Timeframe:</label>
            <CustomSelect
                          id="timeframe-select"
                          options={timeframes}
                          value={selectedTimeframe}
              onChange={(val) => handleTimeframeChange(val)}
                          placeholder="Select Timeframe"
              renderInPortal={false}
                      />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={showPartialTps}
                      onChange={(e) => setShowPartialTps(e.target.checked)}
                    />
                    Show Partial TPs
                  </label>
              </div>
              {/* Trade Filter Panel */}
              <div className="mt-2 p-3 border rounded-md bg-backgroundBody">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-textSecondary">Presets</label>
                    <CustomSelect
                      options={[{ label: 'Load preset…', value: '' }, ...presets.map(p => ({ label: p.name, value: p.id }))]}
                      value={selectedPresetId}
                      onChange={(id) => {
                        setSelectedPresetId(id);
                        const p = presets.find(x => x.id === id);
                        if (p) loadPreset(p);
                      }}
                      renderInPortal={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary">Apply To</label>
                    <CustomSelect
                      options={[{ label: 'Entry', value: 'entry' }, { label: 'Exit', value: 'exit' }]}
                      value={filterForm.apply_to}
                      onChange={(v) => setFilterForm(f => ({ ...f, apply_to: v }))}
                      renderInPortal={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary">Direction</label>
                    <CustomSelect
                      options={[{ label: 'All', value: '' }, { label: 'Buy', value: 'BUY' }, { label: 'Sell', value: 'SELL' }, { label: 'Long', value: 'LONG' }, { label: 'Short', value: 'SHORT' }]}
                      value={filterForm.direction || ''}
                      onChange={(v) => setFilterForm(f => ({ ...f, direction: v || undefined }))}
                      renderInPortal={false}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="block text-xs text-textSecondary">Scoped Apply</span>
                    <div className="flex items-center gap-3 text-[11px]">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={Array.isArray(filterForm.apply_conditions_to_directions) ? filterForm.apply_conditions_to_directions.includes('BUY') : false}
                          onChange={(e) => {
                            setFilterForm(f => {
                              // If legacy direction selected, ignore scoped toggles
                              if (f.direction) return f;
                              let arr = Array.isArray(f.apply_conditions_to_directions) ? [...f.apply_conditions_to_directions] : [];
                              if (e.target.checked) {
                                if (!arr.includes('BUY')) arr.push('BUY');
                              } else {
                                arr = arr.filter(x => x !== 'BUY');
                              }
                              if (arr.length === 0 || arr.length === 2) arr = undefined; // treat as global
                              return { ...f, apply_conditions_to_directions: arr };
                            });
                          }}
                          disabled={!!filterForm.direction}
                        /> BUY
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={Array.isArray(filterForm.apply_conditions_to_directions) ? filterForm.apply_conditions_to_directions.includes('SELL') : false}
                          onChange={(e) => {
                            setFilterForm(f => {
                              if (f.direction) return f;
                              let arr = Array.isArray(f.apply_conditions_to_directions) ? [...f.apply_conditions_to_directions] : [];
                              if (e.target.checked) {
                                if (!arr.includes('SELL')) arr.push('SELL');
                              } else {
                                arr = arr.filter(x => x !== 'SELL');
                              }
                              if (arr.length === 0 || arr.length === 2) arr = undefined;
                              return { ...f, apply_conditions_to_directions: arr };
                            });
                          }}
                          disabled={!!filterForm.direction}
                        /> SELL
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary">Logic</label>
                    <CustomSelect
                      options={[{ label: 'AND', value: 'AND' }, { label: 'OR', value: 'OR' }]}
                      value={filterForm.logic}
                      onChange={(v) => setFilterForm(f => ({ ...f, logic: v }))}
                      renderInPortal={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary">Mode</label>
                    <CustomSelect
                      options={[{ label: 'Include matches', value: 'INCLUDE' }, { label: 'Exclude matches', value: 'EXCLUDE' }]}
                      value={filterForm.filter_mode || 'INCLUDE'}
                      onChange={(v) => setFilterForm(f => ({ ...f, filter_mode: v }))}
                      renderInPortal={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary">Timeframe</label>
                    <CustomSelect
                      options={[{ label: 'Use chart TF', value: '' }, ...timeframes]}
                      value={filterForm.timeframe || ''}
                      onChange={(v) => setFilterForm(f => ({ ...f, timeframe: v || null }))}
                      renderInPortal={false}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!filterForm.include_stats}
                      onChange={(e) => setFilterForm(f => ({ ...f, include_stats: e.target.checked }))}
                    />
                    Include stats
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!filterForm.include_breakdown}
                      onChange={(e) => setFilterForm(f => ({ ...f, include_breakdown: e.target.checked }))}
                      disabled={!!filterForm.direction || !Array.isArray(filterForm.apply_conditions_to_directions) || filterForm.apply_conditions_to_directions?.length !== 1}
                    />
                    Breakdown
                  </label>
                  <Button size="sm" variant="secondary" onClick={addCondition}>Add Condition</Button>
                  <Button size="sm" onClick={() => submitFilter()} disabled={filterLoading}>{filterLoading ? 'Filtering…' : 'Apply Filter'}</Button>
                  <Button size="sm" variant="outline" onClick={savePreset} disabled={isSavingPreset}>Save as Preset</Button>
                  {filteredTradesResp?.items && (
                    <Button size="sm" variant="outline" onClick={clearFilterResults}>Clear Results</Button>
                  )}
                </div>

                {(filterError) && <p className="text-destructive text-xs mt-2">{filterError}</p>}
                {filteredTradesResp?.count !== undefined && (
                  <div className="mt-2 text-xs text-textSecondary">
                    {`${filteredTradesResp.count} trades match`} {`· ${filterForm.apply_to}`} {`· ${filterForm.logic}`} {`· ${filterForm.filter_mode || 'INCLUDE'}`} {filterForm.timeframe ? `· ${filterForm.timeframe}` : ''} {filterForm.direction ? `· ${filterForm.direction}` : ''}
                  </div>
                )}
                {/* Show server-returned stats if provided, else client-computed quick metrics */}
                {filteredTradesResp?.stats ? (
                  (() => {
                    const s = filteredTradesResp.stats || {};
                    const fmt = (v, d = 2) => (typeof v === 'number' && isFinite(v) ? v.toFixed(d) : 'N/A');
                    const pct = (v) => (typeof v === 'number' && isFinite(v) ? (v * 100).toFixed(1) + '%' : 'N/A');
                    const joinMap = (m) => (m && typeof m === 'object') ? Object.entries(m).map(([k, v]) => `${k}: ${v}`).join(', ') : '';
                    return (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1 text-xs text-textSecondary">
                        <div><strong>Total Trades:</strong> {s.count ?? 'N/A'}</div>
                        <div><strong>Win Rate:</strong> {pct(s.win_rate)}</div>
                        <div><strong>Wins:</strong> {s.win_count ?? 'N/A'}</div>
                        <div><strong>Losses:</strong> {s.loss_count ?? 'N/A'}</div>
                        {typeof s.breakeven_count === 'number' && (
                          <div><strong>Breakeven:</strong> {s.breakeven_count}</div>
                        )}
                        <div><strong>Total PnL:</strong> {fmt(s.total_pnl)}</div>
                        <div><strong>Avg PnL:</strong> {fmt(s.avg_pnl)}</div>
                        <div><strong>Avg Win:</strong> {fmt(s.avg_win)}</div>
                        <div><strong>Avg Loss:</strong> {fmt(s.avg_loss)}</div>
                        {s.by_direction && (
                          <div className="md:col-span-2 lg:col-span-3"><strong>By Direction:</strong> {joinMap(s.by_direction)}</div>
                        )}
                        {s.by_status && (
                          <div className="md:col-span-2 lg:col-span-3"><strong>By Status:</strong> {joinMap(s.by_status)}</div>
                        )}
                        {s.by_closure_reason && (
                          <div className="md:col-span-2 lg:col-span-3"><strong>By Reason:</strong> {joinMap(s.by_closure_reason)}</div>
                        )}
                        {filteredTradesResp.breakdown && (
                          <div className="md:col-span-2 lg:col-span-6 mt-2">
                            <div className="border-t pt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                              {['unaffected','affected_pass','affected_fail'].map(key => {
                                const seg = filteredTradesResp.breakdown[key];
                                if (!seg) return null;
                                return (
                                  <div key={key} className="p-2 rounded bg-backgroundSurface/50 border text-[11px]">
                                    <div className="font-semibold">{key.replace('_',' ')}</div>
                                    <div>Count: {seg.count ?? 'N/A'}</div>
                                    {typeof seg.total_pnl === 'number' && <div>Total PnL: {fmt(seg.total_pnl)}</div>}
                                    {typeof seg.avg_pnl === 'number' && <div>Avg PnL: {fmt(seg.avg_pnl)}</div>}
                                    {typeof seg.win_rate === 'number' && <div>Win Rate: {pct(seg.win_rate)}</div>}
                                  </div>
                                );
                              })}
                              {filteredTradesResp.breakdown.meta && (
                                <div className="p-2 rounded bg-backgroundSurface/50 border text-[11px] md:col-span-3">
                                  <div className="font-semibold">Meta</div>
                                  <div>Scoped dirs: {Array.isArray(filteredTradesResp.breakdown.meta.scoped_applied_to) ? filteredTradesResp.breakdown.meta.scoped_applied_to.join(', ') : 'N/A'}</div>
                                  <div>Filter mode: {filteredTradesResp.breakdown.meta.filter_mode}</div>
                                  <div>Affected pass: {filteredTradesResp.breakdown.meta.affected_pass_count}</div>
                                  <div>Affected fail: {filteredTradesResp.breakdown.meta.affected_fail_count}</div>
                                  <div>Unaffected: {filteredTradesResp.breakdown.meta.unaffected_count}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : filteredMetrics && (
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-1 text-xs text-textSecondary">
                    <div><strong>Wins:</strong> {filteredMetrics.wins}</div>
                    <div><strong>Losses:</strong> {filteredMetrics.losses}</div>
                    <div><strong>Win Rate:</strong> {(filteredMetrics.winRate * 100).toFixed(1)}%</div>
                    <div><strong>Avg PnL:</strong> {filteredMetrics.avg.toFixed(2)}</div>
                    <div><strong>Total PnL:</strong> {filteredMetrics.sum.toFixed(2)}</div>
                    <div><strong>Profit Factor:</strong> {Number.isFinite(filteredMetrics.profitFactor) ? filteredMetrics.profitFactor.toFixed(2) : '∞'}</div>
                  </div>
                )}

                {(filterForm.conditions || []).map((cond, idx) => (
                  <div key={idx} className="mt-3 p-2 border rounded flex flex-col gap-2">
                    <div className="flex flex-wrap items-start gap-3">
                      {/* Left operand type */}
                      <div className="flex items-center gap-2">
                        <CustomSelect
                          options={[{ label: 'Indicator', value: 'indicator' }, { label: 'Price', value: 'price' }, { label: 'Number', value: 'number' }]}
                          value={cond.left?.type || 'indicator'}
                          onChange={(v) => {
                            if (v === 'price') updateCondition(idx, { left: { type: 'price', field: 'close' } });
                            else if (v === 'number') updateCondition(idx, { left: { type: 'number', value: 0 } });
                            else updateCondition(idx, { left: { type: 'indicator', name: 'EMAIndicator', output: 'ema', params: { length: 21, src: 'close' }, input: { type: 'price', source: 'close' } } });
                          }}
                          renderInPortal={false}
                        />
                        {cond.left?.type === 'price' && (
                          <CustomSelect
                            options={[{label:'open',value:'open'},{label:'high',value:'high'},{label:'low',value:'low'},{label:'close',value:'close'},{label:'volume',value:'volume'}]}
                            value={cond.left?.field || 'close'}
                            onChange={(v) => updateCondition(idx, { left: { type: 'price', field: v } })}
                            renderInPortal={false}
                          />
                        )}
                        {cond.left?.type === 'number' && (
                          <input type="number" className="w-24 bg-backgroundSurface border rounded px-2 py-1 text-sm"
                            value={cond.left?.value ?? 0}
                            onChange={(e) => updateCondition(idx, { left: { type: 'number', value: Number(e.target.value) } })}
                          />
                        )}
                      </div>

                      {cond.left?.type === 'indicator' && (
                        <div className="min-w-[290px] flex-1">
                          <IndicatorSelector
                            dense
                            value={cond.left && cond.left.type === 'indicator' ? cond.left : { type: 'indicator', name: 'EMAIndicator', output: 'ema', params: { length: 21, src: 'close' }, input: { type: 'price', source: 'close' } }}
                            onChange={(val) => updateCondition(idx, { left: val })}
                          />
                        </div>
                      )}

                      {/* Operator */}
                      <div className="flex items-center">
                        <CustomSelect
                          options={[
                            {label:'>',value:'>'},
                            {label:'<',value:'<'},
                            {label:'>=',value:'>='},
                            {label:'<=',value:'<='},
                            {label:'==',value:'=='},
                            {label:'!=',value:'!='},
                          ]}
                          value={cond.op || '>'}
                          onChange={(v) => updateCondition(idx, { op: v })}
                          renderInPortal={false}
                        />
                      </div>

                      {/* Right operand type */}
                      <div className="flex items-center gap-2">
                        <CustomSelect
                          options={[{ label: 'Indicator', value: 'indicator' }, { label: 'Price', value: 'price' }, { label: 'Number', value: 'number' }]}
                          value={cond.right?.type || 'indicator'}
                          onChange={(v) => {
                            if (v === 'price') updateCondition(idx, { right: { type: 'price', field: 'close' } });
                            else if (v === 'number') updateCondition(idx, { right: { type: 'number', value: 0 } });
                            else updateCondition(idx, { right: { type: 'indicator', name: 'EMAIndicator', output: 'ema', params: { length: 50, src: 'close' }, input: { type: 'price', source: 'close' } } });
                          }}
                          renderInPortal={false}
                        />
                        {cond.right?.type === 'price' && (
                          <CustomSelect
                            options={[{label:'open',value:'open'},{label:'high',value:'high'},{label:'low',value:'low'},{label:'close',value:'close'},{label:'volume',value:'volume'}]}
                            value={cond.right?.field || 'close'}
                            onChange={(v) => updateCondition(idx, { right: { type: 'price', field: v } })}
                            renderInPortal={false}
                          />
                        )}
                        {cond.right?.type === 'number' && (
                          <input type="number" className="w-24 bg-backgroundSurface border rounded px-2 py-1 text-sm"
                            value={cond.right?.value ?? 0}
                            onChange={(e) => updateCondition(idx, { right: { type: 'number', value: Number(e.target.value) } })}
                          />
                        )}
                      </div>

                      {cond.right?.type === 'indicator' && (
                        <div className="min-w-[290px] flex-1">
                          <IndicatorSelector
                            dense
                            value={cond.right && cond.right.type === 'indicator' ? cond.right : { type: 'indicator', name: 'EMAIndicator', output: 'ema', params: { length: 50, src: 'close' }, input: { type: 'price', source: 'close' } }}
                            onChange={(val) => updateCondition(idx, { right: val })}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => removeCondition(idx)}>Remove</Button>
                    </div>
                  </div>
                ))}
              </div>
        <IndicatorControls
                  isBacktest={true}
          onAddIndicator={handleAddIndicator}
                  onRemoveIndicator={handleRemoveIndicator}
                  // Adapt for IndicatorControls, which expects an `id` property
                  activeIndicators={activeIndicators.map(i => ({ ...i, id: i.name }))} 
                  availableIndicators={availableIndicators}
              />
              {chartData && chartData.ohlcv_data && chartData.ohlcv_data.length > 0 ? (
                <TradingChart
                  // key removed to avoid remounting on unrelated state changes
                  ohlcvData={ohlcvData}
                  tradeMarkers={tradeMarkers}
                  extraMarkers={partialTpMarkers}
                  backtestIndicatorData={chartData?.indicator_data || {}}
                  isBacktest={true}
                  selectedSymbol={runDetails.instrument_symbol}
                  selectedTimeframe={selectedTimeframe} // Use state for timeframe
                  height={600} // Increased height for better visibility with panes
                  selectedTrade={selectedTrade}
                  onBarSelected={(idx) => { setSelectedBarIndex(idx); setActiveTab('Explain'); }}
                />
              ) : (
                <div style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                  {isLoading ? 'Loading chart data...' : 'No chart data available for this run.'}
                </div>
              )}
              {/* Add a small bar selector under the chart to drive Explain/Trace */}
              <div className="mt-4 flex items-end gap-3">
                <div>
                  <label className="block text-xs text-textSecondary">Selected Bar Index</label>
                  <input
                    type="number"
                    className="border rounded px-2 py-1 text-sm bg-background"
                    placeholder="e.g. 5"
                    value={selectedBarIndex ?? ''}
                    onChange={(e) => setSelectedBarIndex(e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('Explain')}>Explain</Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('Trace')}>Trace</Button>
              </div>
            </div>
          </section>
        )}

        {/* Tabs: Explain | Trace */}
        <section>
          <div className="flex items-center gap-2 border-b mt-6">
            <Tab title="Explain" isActive={activeTab === 'Explain'} onClick={() => setActiveTab('Explain')} />
            <Tab title="Trace" isActive={activeTab === 'Trace'} onClick={() => setActiveTab('Trace')} />
          </div>
          <div className="pt-4">
            {activeTab === 'Explain' && (
              <ExplainPanel data={explainData} loading={explainLoading} error={explainError} />
            )}
            {activeTab === 'Trace' && (
              <div className="space-y-4">
                <TraceFilters
                  value={{ ...traceParams, bar_index: traceParams.bar_index ?? selectedBarIndex }}
                  onChange={setTraceParams}
                  onApply={handleApplyTraceFilters}
                  onClear={handleClearTraceFilters}
                  disabled={traceLoading}
                />
                <TracePanel
                  items={traceItems}
                  count={traceCount}
                  loading={traceLoading}
                  error={traceError}
                  page={traceParams.offset / (traceParams.limit || 200) + 1}
                  setPage={() => {}}
                  pageSize={traceParams.limit || 200}
                  onLoadMore={handleLoadMoreTrace}
                  onSelectItem={(it) => { setSelectedBarIndex(it?.bar_index); setActiveTab('Explain'); }}
                />
              </div>
            )}
          </div>
        </section>

  {(filteredTradesResp?.items?.length || runDetails.simulated_trades_log?.length) > 0 && (
          <section>
            <h2 className="text-xl font-semibold text-textPrimary mb-2">Simulated Trades Log</h2>
            <div className="bg-backgroundSurface p-4 rounded-lg shadow">
              {/* Filters and Controls */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                {/* Filters */}
                <CustomSelect
                  options={[{ value: 'all', label: 'All Directions' }, { value: 'buy', label: 'Buy' }, { value: 'sell', label: 'Sell' }]}
                  value={filters.direction}
                  onChange={(v) => setFilters(f => ({ ...f, direction: v }))}
                />
                <CustomSelect
                  options={closureReasonOptions.map(r => ({ value: r, label: r === 'all' ? 'All Reasons' : r }))}
                  value={filters.closure_reason}
                  onChange={(v) => setFilters(f => ({ ...f, closure_reason: v }))}
                />
                <label className="text-xs flex items-center gap-2">
                  <input type="checkbox" checked={showFilteredOnly} onChange={(e) => setShowFilteredOnly(e.target.checked)} />
                  Show filtered only
                </label>
                {/* Pagination Controls */}
                <div className="flex items-center gap-2">
                  <Button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</Button>
                  <span>Page {currentPage} of {totalPages}</span>
                  <Button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</Button>
                </div>
                <CustomSelect
                  options={[{ value: 10, label: '10/page' }, { value: 25, label: '25/page' }, { value: 50, label: '50/page' }]}
                  value={itemsPerPage}
                  onChange={(v) => setItemsPerPage(Number(v))}
                />
                {filteredTradesResp?.count !== undefined && (
                  <span className="text-xs text-textSecondary">Filtered count: {filteredTradesResp.count}</span>
                )}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-borderDefault">
                  <thead className="bg-backgroundBody">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">{''}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Symbol</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider cursor-pointer" onClick={() => requestSort('direction')}>Direction</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider cursor-pointer" onClick={() => requestSort('volume')}>Volume</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider cursor-pointer" onClick={() => requestSort('entry_timestamp')}>Entry Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Entry Price</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider" title="Spread cost at entry snapshot">Entry Spread Cost</th>
                      <th className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">SL</th>
                      <th className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">TP</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider cursor-pointer" onClick={() => requestSort('exit_timestamp')}>Exit Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Exit Price</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider cursor-pointer" onClick={() => requestSort('realized_pnl')}>Net P&L</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider" title="Commission deducted on this trade">Commission</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider" title="Spread cost deducted on this trade">Spread</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Closure Reason</th>
                      <th className="px-3 py-2 text-xs text-textPrimary break-all">Comment</th>
                    </tr>
                  </thead>
                  <tbody className="bg-backgroundSurface divide-y divide-borderDefault">
                    {filteredAndSortedTrades.map((trade, idx) => {
                      const rowKey = trade.id ? `${trade.id}` : `row-${idx}-${trade.entry_timestamp || ''}-${trade.exit_timestamp || ''}`;
                      const hasTpPlan = Array.isArray(trade.tp_plan) && trade.tp_plan.length > 0;
                      const isExpanded = trade.id && expandedTrades.has(trade.id);
                      const displayPnl = (typeof trade.realized_pnl === 'number') ? trade.realized_pnl : trade.pnl;
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            onClick={() => setSelectedTrade(trade)}
                            className={`cursor-pointer ${selectedTrade?.id === trade.id ? 'bg-blue-900' : ''}`}
                          >
                            <td className="px-2 py-2 whitespace-nowrap text-xs text-textPrimary">
                              {hasTpPlan ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleExpandTrade(trade.id); }}
                                  className="text-primary hover:underline"
                                  title={isExpanded ? 'Collapse TP plan' : 'Expand TP plan'}
                                >
                                  {isExpanded ? '−' : '+'}
                                </button>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.symbol}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.direction}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{(trade.volume && trade.volume !== 0) ? trade.volume : (trade.reduced_volume ?? trade.volume)}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.entry_timestamp ? new Date(trade.entry_timestamp).toLocaleString() : ''}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.entry_price}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{typeof trade.entry_spread_cost === 'number' ? trade.entry_spread_cost.toFixed(2) : (trade.entry_spread_cost ?? 'N/A')}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.stop_loss}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.take_profit}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.exit_timestamp ? new Date(trade.exit_timestamp).toLocaleString() : 'N/A'}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.exit_price !== null ? trade.exit_price : 'N/A'}</td>
                            <td className={`px-3 py-2 whitespace-nowrap text-xs ${displayPnl < 0 ? 'text-destructive' : 'text-success'}`}>{typeof displayPnl === 'number' ? displayPnl.toFixed(2) : 'N/A'}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{typeof trade.commission_cost === 'number' ? trade.commission_cost.toFixed(2) : (trade.commission_cost ?? 'N/A')}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{typeof trade.spread_cost === 'number' ? trade.spread_cost.toFixed(2) : (trade.spread_cost ?? 'N/A')}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.status}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-textPrimary">{trade.closure_reason || 'N/A'}</td>
                            <td className="px-3 py-2 text-xs text-textPrimary break-all">{trade.comment || 'Comment'}</td>
                          </tr>
                          {isExpanded && hasTpPlan && (
                            <tr className="bg-backgroundBody">
                              <td colSpan={17} className="px-6 py-2 text-xs text-textPrimary">
                                <div className="font-semibold mb-1">TP Plan</div>
                                <div className="grid grid-cols-7 gap-2">
                                  <div className="font-semibold">Tag</div>
                                  <div className="font-semibold">Price</div>
                                  <div className="font-semibold">P&L</div>
                                  <div className="font-semibold">Close Vol</div>
                                  <div className="font-semibold">Move SL</div>
                                  <div className="font-semibold">Status</div>
                                  <div className="font-semibold">Hit?</div>
                                  {trade.tp_plan.map((tp, i) => (
                                    <React.Fragment key={i}>
                                      <div>{tp.tag || 'TP'}</div>
                                      <div>{typeof tp.price === 'number' ? tp.price : 'N/A'}</div>
                                      <div className={tp.pnl < 0 ? 'text-destructive' : 'text-success'}>{typeof tp.pnl === 'number' ? tp.pnl.toFixed(2) : 'N/A'}</div>
                                      <div>{tp.close_volume ?? 'N/A'}</div>
                                      <div>{tp.move_sl ? (tp.move_sl.mode || 'yes') : '-'}</div>
                                      <div>{tp.status || 'N/A'}</div>
                                      <div>{tp.status === 'HIT' ? '✅' : ''}</div>
                                    </React.Fragment>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
};

const Tab = ({ title, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium ${
      isActive ? 'border-b-2 border-primary text-primary' : 'text-textSecondary hover:bg-primary/10'
    }`}
  >
    {title}
  </button>
);

export default BacktestRunDetailPage;
